import { BhaSha } from "@bhashaime/core";

const bhaShaEngine = new BhaSha();

export const BHASHA_REGIONAL_LANGUAGES = ["hindi", "gujarati"];

export const REGIONAL_LANGUAGE_OPTIONS = [
  //Created By Cursor — Google Input Tools for names; dictionary for meaning words
  { id: "hindi", label: "Hindi", nativeLabel: "हिन्दी", engine: "google", code: "hi-t-i0-und", fallback: "bhasha" },
  { id: "gujarati", label: "Gujarati", nativeLabel: "ગુજરાતી", engine: "google", code: "gu-t-i0-und", fallback: "bhasha" },
  { id: "marathi", label: "Marathi", nativeLabel: "मराठी", engine: "google", code: "mr-t-i0-und" },
  { id: "tamil", label: "Tamil", nativeLabel: "தமிழ்", engine: "google", code: "ta-t-i0-und" },
  { id: "telugu", label: "Telugu", nativeLabel: "తెలుగు", engine: "google", code: "te-t-i0-und" },
  { id: "kannada", label: "Kannada", nativeLabel: "ಕನ್ನಡ", engine: "google", code: "kn-t-i0-und" },
  { id: "malayalam", label: "Malayalam", nativeLabel: "മലയാളം", engine: "google", code: "ml-t-i0-und" },
  { id: "punjabi", label: "Punjabi", nativeLabel: "ਪੰਜਾਬੀ", engine: "google", code: "pa-t-i0-und" },
  { id: "bengali", label: "Bengali", nativeLabel: "বাংলা", engine: "google", code: "bn-t-i0-und" },
];

//Created By Cursor — meaning dictionary (not phonetic): Good→अच्छा, Indian→भारतीय
const HINDI_MEANING_PHRASES = [
  ["parents need", "अभिभावक की आवश्यकता"],
  ["parent need", "अभिभावक की आवश्यकता"],
  ["personal reason", "व्यक्तिगत कारण"],
  ["family reason", "पारिवारिक कारण"],
  ["change of residence", "निवास परिवर्तन"],
  ["very good", "बहुत अच्छा"],
  ["very excellent", "अत्यंत उत्कृष्ट"],
];

const HINDI_MEANING_WORDS = {
  good: "अच्छा",
  excellent: "उत्कृष्ट",
  outstanding: "उत्कृष्ट",
  satisfactory: "संतोषजनक",
  average: "औसत",
  fair: "सामान्य",
  poor: "कमजोर",
  indian: "भारतीय",
  hindi: "हिन्दी",
  english: "अंग्रेज़ी",
  parents: "अभिभावक",
  parent: "अभिभावक",
  need: "आवश्यकता",
  reason: "कारण",
  leaving: "प्रस्थान",
  conduct: "आचरण",
  progress: "प्रगति",
  language: "भाषा",
  remark: "टिप्पणी",
  boy: "बालक",
  girl: "बालिका",
  male: "पुरुष",
  female: "महिला",
  hindu: "हिन्दू",
  muslim: "मुस्लिम",
  sikh: "सिख",
  christian: "ईसाई",
  general: "सामान्य",
  obc: "अन्य पिछड़ा वर्ग",
  sc: "अनुसूचित जाति",
  st: "अनुसूचित जनजाति",
  yes: "हाँ",
  no: "नहीं",
};

const GUJARATI_MEANING_PHRASES = [
  ["parents need", "વાલીની જરૂરિયાત"],
  ["very good", "ખૂબ સારું"],
];

const GUJARATI_MEANING_WORDS = {
  good: "સારું",
  excellent: "ઉત્તમ",
  indian: "ભારતીય",
  hindi: "હિન્દી",
  english: "અંગ્રેજી",
  parents: "વાલી",
  conduct: "વર્તન",
  progress: "પ્રગતિ",
  language: "ભાષા",
  remark: "ટિપ્પણી",
};

const MEANING_BY_LANGUAGE = {
  hindi: { phrases: HINDI_MEANING_PHRASES, words: HINDI_MEANING_WORDS },
  gujarati: { phrases: GUJARATI_MEANING_PHRASES, words: GUJARATI_MEANING_WORDS },
};

const googleLanguageMap = REGIONAL_LANGUAGE_OPTIONS.reduce((acc, item) => {
  if (item.code) {
    acc[item.id] = item.code;
  }
  return acc;
}, {});

export function getRegionalLanguageOption(languageId) {
  return (
    REGIONAL_LANGUAGE_OPTIONS.find((item) => item.id === languageId) ||
    REGIONAL_LANGUAGE_OPTIONS[0]
  );
}

//Created By Cursor
function normalizeForTransliteration(text) {
  return String(text || "")
    .replace(/\s+/g, " ")
    .trim();
}

//Created By Cursor — already Devanagari / Indic script? skip
function hasIndicScript(text) {
  return /[\u0900-\u097F\u0A80-\u0AFF\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F\u0A00-\u0A7F\u0980-\u09FF]/.test(
    String(text || "")
  );
}

//Created By Cursor — apply meaning dictionary first (phrases, then words)
function applyMeaningDictionary(text, languageId) {
  const pack = MEANING_BY_LANGUAGE[languageId];
  if (!pack) {
    return null;
  }

  let remaining = normalizeForTransliteration(text);
  if (!remaining) {
    return "";
  }

  const lowerFull = remaining.toLowerCase();
  for (const [phrase, meaning] of pack.phrases) {
    if (lowerFull === phrase) {
      return meaning;
    }
  }

  //Created By Cursor — replace known phrases inside longer text
  let working = remaining;
  for (const [phrase, meaning] of pack.phrases) {
    const re = new RegExp(`\\b${phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "gi");
    working = working.replace(re, meaning);
  }

  const tokens = working.split(/(\s+)/);
  let usedDictionary = working !== remaining;

  const mapped = tokens.map((token) => {
    if (!token || /^\s+$/.test(token) || hasIndicScript(token)) {
      return token;
    }
    const clean = token.replace(/[^a-zA-Z]/g, "");
    if (!clean) {
      return token;
    }
    const meaning = pack.words[clean.toLowerCase()];
    if (!meaning) {
      return token;
    }
    usedDictionary = true;
    return token.replace(clean, meaning);
  });

  if (!usedDictionary) {
    return null;
  }
  return mapped.join("");
}

export function transliterateWithBhaSha(text, languageId) {
  if (!text) {
    return "";
  }

  if (!BHASHA_REGIONAL_LANGUAGES.includes(languageId)) {
    return text;
  }

  try {
    bhaShaEngine.setLanguage(languageId);
    const phonetic = String(text).toLowerCase();
    const out = bhaShaEngine.transliterateText(phonetic);
    return out || text;
  } catch (error) {
    console.error("BhaSha transliteration failed:", error);
    return String(text);
  }
}

//Created By Cursor — single chunk via Google Input Tools
async function transliterateChunkWithGoogle(text, itc) {
  const response = await fetch(
    `https://inputtools.google.com/request?text=${encodeURIComponent(
      text
    )}&itc=${itc}&num=1&cp=0&cs=1&ie=utf-8&oe=utf-8&app=demopage`
  );
  const data = await response.json();
  if (data[0] === "SUCCESS" && data[1]?.[0]?.[1]?.[0]) {
    return data[1][0][1][0];
  }
  return "";
}

export async function transliterateWithGoogle(text, languageId) {
  const normalized = normalizeForTransliteration(text);
  if (!normalized) {
    return "";
  }

  const itc = googleLanguageMap[languageId];
  if (!itc) {
    return text;
  }

  try {
    const parts = normalized.split(" ");
    const translatedParts = [];

    for (const part of parts) {
      if (!part) {
        translatedParts.push("");
        continue;
      }
      if (hasIndicScript(part)) {
        translatedParts.push(part);
        continue;
      }

      const pack = MEANING_BY_LANGUAGE[languageId];
      const dictHit = pack?.words?.[part.toLowerCase().replace(/[^a-zA-Z]/g, "")];
      if (dictHit) {
        translatedParts.push(dictHit);
        continue;
      }

      const lower = part.toLowerCase();
      let translated = await transliterateChunkWithGoogle(lower, itc);
      if (!translated) {
        translated = await transliterateChunkWithGoogle(part, itc);
      }
      translatedParts.push(translated || part);
    }

    const joined = translatedParts.join(" ").trim();
    if (joined && joined !== normalized.toLowerCase()) {
      return joined;
    }

    const whole =
      (await transliterateChunkWithGoogle(normalized.toLowerCase(), itc)) ||
      (await transliterateChunkWithGoogle(normalized, itc));
    return whole || normalized;
  } catch (error) {
    console.error("Regional transliteration failed:", error);
  }

  return text;
}

export async function transliterateEnglishToRegional(text, languageId) {
  const normalized = normalizeForTransliteration(text);
  if (!normalized) {
    return "";
  }

  if (hasIndicScript(normalized)) {
    return normalized;
  }

  const language = getRegionalLanguageOption(languageId);

  //Created By Cursor — meaning first (Good→अच्छा), then phonetic for names
  const meaningOut = applyMeaningDictionary(normalized, language.id);
  if (meaningOut != null && hasIndicScript(meaningOut)) {
    // If mixed (some words still English), finish remaining via Google
    if (/[a-zA-Z]/.test(meaningOut)) {
      const finished = await transliterateWithGoogle(meaningOut, language.id);
      return finished || meaningOut;
    }
    return meaningOut;
  }

  if (language.engine === "google" || language.code) {
    const googleOut = await transliterateWithGoogle(normalized, language.id);
    const googleWorked =
      googleOut &&
      googleOut.trim() &&
      googleOut.trim().toLowerCase() !== normalized.toLowerCase();

    if (googleWorked) {
      return googleOut;
    }

    if (language.fallback === "bhasha" || BHASHA_REGIONAL_LANGUAGES.includes(language.id)) {
      return transliterateWithBhaSha(normalized, language.id);
    }

    return googleOut || normalized;
  }

  if (language.engine === "bhasha") {
    return transliterateWithBhaSha(normalized, language.id);
  }

  return transliterateWithGoogle(normalized, language.id);
}

export function getRegionalFieldKey(fieldKey) {
  return `${fieldKey}Regional`;
}
